import { OperationResponse } from "api/actor";
import { mapLegalEntityFormValues } from "features/new-actor/mappers/optim/mapLegalEntityFormValues";
import { mapNaturalPersonFormValues } from "features/new-actor/mappers/optim/mapNaturalPersonFormValues";
import { LegalEntityFormValues } from "features/new-actor/types/optim/legalEntityFormValues";
import { NaturalPersonFormValues } from "features/new-actor/types/optim/naturalPersonFormValues";
import { selectReferentialData } from "features/referentials/selectors/selectReferentialDataByKey";
import { FormikProps } from "formik";
import { RefObject } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { actorsAPI } from "services/swagger-api";
import { risksAPI } from "services/swagger-api/riskApi";
import {
  applyValidationErrorsToForm,
  createPersonFieldResolver,
  parseApiError,
} from "shared/utils/apiValidationErrors";

/**
 * Maps a backend validation member onto an actor Formik field. Handles the
 * personne-morale contacts (`naturalPersons[0].address...` -> `contacts.0.address`)
 * and otherwise falls back to the flat person fields.
 */
const resolveActorField = (member: string): string | undefined => {
  const m = member.toLowerCase();
  // Le back peut renvoyer le nom JSON (naturalPersons) ou le nom C#
  // (PersonnesPhysiques) selon la couche qui a produit l'erreur.
  if (m.includes('naturalperson') || m.includes('personnesphysiques')) {
    const idx =
      member.match(/\[(\d+)\]/)?.[1] ??
      member.match(/(?:^|\.)(\d+)(?:\.|$)/)?.[1];
    if (idx !== undefined) {
      if (m.includes('address') || m.includes('adresse'))
        return `contacts.${idx}.address`;
      if (m.includes('email')) return `contacts.${idx}.email`;
      if (m.includes('phone') || m.includes('telephone'))
        return `contacts.${idx}.phone`;
    }
  }
  return createPersonFieldResolver('address')(member);
};

export const useSubmitInsured = (
  formikRef?: RefObject<FormikProps<any>> | null
) => {
  const referentialData = useSelector(
    selectReferentialData
  );

  // Remonte un 400 du back sur les champs Formik (en rouge) + toast lisible.
  const handleSubmitError = async (error: unknown) => {
    const parsed = await parseApiError(error);
    const matched = formikRef?.current
      ? applyValidationErrorsToForm({
          validationErrors: parsed.validationErrors,
          setFieldError: formikRef.current.setFieldError,
          setFieldTouched: formikRef.current.setFieldTouched,
          resolveField: resolveActorField,
        })
      : 0;
    toast.error(
      parsed.message ||
        (matched > 0
          ? 'Veuillez corriger les champs en erreur'
          : 'Erreur lors de la sauvegarde')
    );
  };

  const submitNaturalPerson = async (
    values: NaturalPersonFormValues,
    riskId?: string,
    plateformeId?: string,
  ): Promise<OperationResponse | undefined> => {
    try {
      const createNaturalPersonCommandDto = mapNaturalPersonFormValues(
        values,
        referentialData,
      );
      // Rattachement explicite à une OU (cas admin créant un souscripteur pour le compte
      // d'une plateforme cible) — la PP devient alors visible par les gestionnaires de l'OU.
      // Le mapper est typé NaturalPersonDto (lecture) ; on étend dynamiquement avec le champ
      // `plateformeId` accepté côté back par CreateNaturalPersonCommandDto.
      if (plateformeId) {
        (createNaturalPersonCommandDto as any).plateformeId = plateformeId;
      }
      if (riskId) {
        return await risksAPI.createInsuredPersonByBranchCode({
          createInsuredPersonCommandDto: {
            naturalPerson: createNaturalPersonCommandDto,
            riskId
          }
        });
      }

      return await actorsAPI.createNaturalPerson({
        createNaturalPersonCommandDto
      });
    } catch (error: any) {
      await handleSubmitError(error);
    }
  };

  const submitLegalEntity = async (
    values: LegalEntityFormValues,
    riskId?: string,
    plateformeId?: string,
  ): Promise<OperationResponse | undefined> => {
    try {
      const createLegalEntityCommandDto = mapLegalEntityFormValues(
        values,
        referentialData,
      );
      // Idem PP : on étend dynamiquement (mapper typé LegalEntityDto, le back accepte
      // `plateformeId` via CreateLegalEntityCommandDto).
      if (plateformeId) {
        (createLegalEntityCommandDto as any).plateformeId = plateformeId;
      }

      if (riskId) {
        return await risksAPI.createInsuredPersonByBranchCode({
          createInsuredPersonCommandDto: {
            legalEntity: createLegalEntityCommandDto,
            riskId
          }
        });
      }

      return await actorsAPI.createLegalEntity({
        createLegalEntityCommandDto
      });
    } catch (error: any) {
      await handleSubmitError(error);
    }
  };

  return { submitNaturalPerson, submitLegalEntity };
};
